import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 心理学 - 横屏「情绪光谱」风格
 *
 * 核心亮点：
 * - SVG 情绪波动折线图（心电图风格）
 * - 渐变色谱带 + 滑块指示器动画
 * - 情绪面孔从😢→😐→😊的渐变
 * - 阶段标注 + 触发因素气泡
 * - 实时"当前情绪指数"仪表
 *
 * 尺寸：1920x1080 横屏
 */

interface EmotionZone {
  label: string;
  emoji: string;
  color: string;
  description: string;
  position: number; // 0-100
}

interface Trigger {
  text: string;
  icon: string;
  impact: 'positive' | 'negative' | 'neutral';
}

interface PsychologySpectrumProps {
  conceptName: string;
  conceptNameEn: string;
  zones: EmotionZone[];
  triggers: Trigger[];
  waveData: number[];
  currentMood: number;
  insight: string;
  tip: string;
}

const DEFAULT_PROPS: PsychologySpectrumProps = {
  conceptName: '情绪调节的窗口容忍度',
  conceptNameEn: 'Window of Tolerance',
  zones: [
    { label: '过度激活', emoji: '😰', color: '#ef4444', description: '焦虑、恐慌、过度警觉', position: 90 },
    { label: '最佳唤醒', emoji: '😊', color: '#22c55e', description: '平静、专注、有掌控感', position: 50 },
    { label: '低度激活', emoji: '😶', color: '#3b82f6', description: '麻木、抑郁、缺乏动力', position: 15 },
  ],
  triggers: [
    { text: '被老板批评', icon: '😤', impact: 'negative' },
    { text: '深呼吸 3 次', icon: '🧘', impact: 'positive' },
    { text: '收到好消息', icon: '🎉', impact: 'positive' },
    { text: '睡眠不足', icon: '😴', impact: 'negative' },
  ],
  waveData: [50, 55, 48, 65, 80, 92, 88, 75, 60, 45, 30, 18, 25, 35, 48, 55, 50, 52, 48, 50],
  currentMood: 50,
  insight: '大多数情绪反应都是正常的——关键是学会在"窗口"内调节，而不是压抑情绪',
  tip: '当情绪飙升时：暂停 → 深呼吸 → 命名情绪 → 选择回应方式',
};

export const PsychologySpectrum: React.FC<Partial<PsychologySpectrumProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Phases
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Spectrum bar (30-80)
  const spectrumDraw = interpolate(frame, [30, 80], [0, 1], { extrapolateRight: 'clamp' });

  // Wave chart (80-220)
  const waveDraw = interpolate(frame, [80, 220], [0, 1], { extrapolateRight: 'clamp' });

  // Zones (100-200)
  const zoneStart = 100;

  // Triggers (230-370)
  const triggerStart = 230;
  const triggerInterval = 35;

  // Insight (380+)
  const insightOpacity = interpolate(frame, [380, 410], [0, 1], { extrapolateRight: 'clamp' });

  // Tip
  const tipOpacity = interpolate(frame, [420, 450], [0, 1], { extrapolateRight: 'clamp' });

  // Slider animation on spectrum
  const sliderPos = interpolate(frame, [50, 400], [50, 50], { extrapolateRight: 'clamp' });
  // Simulate mood fluctuations
  const moodWave = 50 + 35 * Math.sin(frame * 0.04) * interpolate(frame, [50, 200], [0, 1], { extrapolateRight: 'clamp' });

  // Wave chart geometry
  const waveW = 1260;
  const waveH = 280;
  const wavePad = 30;

  const wavePoints = p.waveData.map((v, i) => {
    const x = wavePad + (i / (p.waveData.length - 1)) * (waveW - wavePad * 2);
    const y = waveH - wavePad - (v / 100) * (waveH - wavePad * 2);
    return { x, y };
  });
  const wavePathD = wavePoints.map((pt, i) => {
    if (i === 0) return `M${pt.x},${pt.y}`;
    const prev = wavePoints[i - 1];
    const cpx = (prev.x + pt.x) / 2;
    return `C${cpx},${prev.y} ${cpx},${pt.y} ${pt.x},${pt.y}`;
  }).join(' ');

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 38,
          left: 60,
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ padding: '6px 16px', background: '#6366f118', border: '1px solid #6366f140', borderRadius: 10, fontSize: 20, color: '#818cf8', fontWeight: 600, letterSpacing: '0.06em' }}>
            PSYCHOLOGY
          </div>
        </div>
        <h1 style={{ fontSize: 46, fontWeight: 800, margin: 0, lineHeight: 1.15 }}>{p.conceptName}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', marginTop: 6, fontStyle: 'italic' }}>{p.conceptNameEn}</p>
      </div>

      {/* ========== EMOTION SPECTRUM BAR ========== */}
      <div
        style={{
          position: 'absolute',
          top: 320,
          left: 60,
          right: 60,
        }}
      >
        {/* Gradient bar */}
        <div
          style={{
            height: 44,
            borderRadius: 22,
            background: `linear-gradient(90deg, #3b82f6, #22c55e, #fbbf24, #ef4444)`,
            opacity: spectrumDraw,
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {/* Zone labels */}
          {p.zones.map((zone, i) => {
            const opacity = interpolate(frame, [zoneStart + i * 30, zoneStart + i * 30 + 20], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${zone.position}%`,
                  top: -76,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  opacity,
                }}
              >
                <div style={{ fontSize: 34 }}>{zone.emoji}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: zone.color, whiteSpace: 'nowrap' }}>{zone.label}</div>
              </div>
            );
          })}

          {/* Animated slider */}
          <div
            style={{
              position: 'absolute',
              left: `${moodWave}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 26,
              height: 52,
              borderRadius: 13,
              background: 'white',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              border: '3px solid #1e1b4b',
              zIndex: 2,
            }}
          />
        </div>

        {/* Zone descriptions below */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
          {p.zones.map((zone, i) => {
            const opacity = interpolate(frame, [zoneStart + i * 30 + 10, zoneStart + i * 30 + 25], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ textAlign: 'center', opacity, maxWidth: 280 }}>
                <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.45)' }}>{zone.description}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== EMOTION WAVE CHART (ECG style) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 470,
          left: 60,
          width: waveW,
        }}
      >
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', marginBottom: 12, fontWeight: 500 }}>情绪波动追踪</div>
        <svg width={waveW} height={waveH} viewBox={`0 0 ${waveW} ${waveH}`}>
          {/* Green "safe zone" band */}
          <rect
            x={wavePad}
            y={waveH - wavePad - 0.65 * (waveH - wavePad * 2)}
            width={(waveW - wavePad * 2) * waveDraw}
            height={0.3 * (waveH - wavePad * 2)}
            fill="#22c55e"
            opacity="0.06"
            rx="8"
          />
          <text
            x={waveW - wavePad + 8}
            y={waveH - wavePad - 0.5 * (waveH - wavePad * 2)}
            fill="#22c55e"
            fontSize="14"
            opacity="0.4"
          >
            安全区
          </text>

          {/* Grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <line
              key={t}
              x1={wavePad}
              y1={waveH - wavePad - t * (waveH - wavePad * 2)}
              x2={waveW - wavePad}
              y2={waveH - wavePad - t * (waveH - wavePad * 2)}
              stroke="rgba(255,255,255,0.04)"
              strokeWidth="1"
            />
          ))}

          {/* Wave line */}
          <path
            d={wavePathD}
            fill="none"
            stroke="#818cf8"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="2000"
            strokeDashoffset={2000 * (1 - waveDraw)}
          />
          {/* Wave glow */}
          <path
            d={wavePathD}
            fill="none"
            stroke="#818cf8"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="2000"
            strokeDashoffset={2000 * (1 - waveDraw)}
            opacity="0.1"
          />

          {/* Data dots */}
          {wavePoints.map((pt, i) => {
            const ptVisible = interpolate(waveDraw, [i / wavePoints.length, (i + 0.5) / wavePoints.length], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            if (ptVisible < 0.1) return null;

            const value = p.waveData[i];
            const isHigh = value > 80;
            const isLow = value < 25;
            const dotColor = isHigh ? '#ef4444' : isLow ? '#3b82f6' : '#818cf8';

            return (
              <g key={i} opacity={ptVisible}>
                <circle cx={pt.x} cy={pt.y} r={isHigh || isLow ? 6 : 4} fill={dotColor} />
                {(isHigh || isLow) && (
                  <circle cx={pt.x} cy={pt.y} r="12" fill={dotColor} opacity="0.15" />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* ========== TRIGGERS (right panel) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 470,
          right: 60,
          width: 420,
        }}
      >
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.35)', marginBottom: 16, fontWeight: 500 }}>常见情绪触发器</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {p.triggers.map((trigger, i) => {
            const start = triggerStart + i * triggerInterval;
            const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 130, mass: 0.5 } });
            const borderColor = trigger.impact === 'positive' ? '#22c55e' : trigger.impact === 'negative' ? '#ef4444' : '#64748b';

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 18px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: 14,
                  border: `1px solid ${borderColor}30`,
                  opacity: progress,
                  transform: `translateX(${(1 - progress) * 30}px)`,
                }}
              >
                <span style={{ fontSize: 30 }}>{trigger.icon}</span>
                <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.7)', flex: 1 }}>{trigger.text}</span>
                <div
                  style={{
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    background: borderColor,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Current mood gauge — top-right corner */}
        <div
          style={{
            position: 'absolute',
            top: -400,
            right: 10,
            padding: '12px 22px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            opacity: interpolate(frame, [300, 320], [0, 1], { extrapolateRight: 'clamp' }),
          }}
        >
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>实时情绪指数</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: moodWave > 70 ? '#ef4444' : moodWave < 30 ? '#3b82f6' : '#22c55e', lineHeight: 1 }}>
            {Math.round(moodWave)}
          </div>
          <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
            {moodWave > 70 ? '过度激活' : moodWave < 30 ? '低度激活' : '最佳区间'}
          </div>
        </div>
      </div>

      {/* ========== INSIGHT + TIP ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 60,
          right: 60,
          display: 'flex',
          gap: 24,
        }}
      >
        <div style={{ flex: 1, opacity: insightOpacity }}>
          <div
            style={{
              padding: '20px 28px',
              background: 'rgba(34,197,94,0.06)',
              border: '1px solid rgba(34,197,94,0.2)',
              borderRadius: 16,
            }}
          >
            <div style={{ fontSize: 24, color: '#22c55e', fontWeight: 600, marginBottom: 8, letterSpacing: '0.08em' }}>INSIGHT</div>
            <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>💡 {p.insight}</div>
          </div>
        </div>
        <div style={{ flex: 1, opacity: tipOpacity }}>
          <div
            style={{
              padding: '20px 28px',
              background: 'rgba(251,191,36,0.06)',
              border: '1px solid rgba(251,191,36,0.2)',
              borderRadius: 16,
            }}
          >
            <div style={{ fontSize: 24, color: '#fbbf24', fontWeight: 600, marginBottom: 8, letterSpacing: '0.08em' }}>HOW TO</div>
            <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>🎯 {p.tip}</div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #3b82f6, #22c55e, #fbbf24, #ef4444)',
        }}
      />
    </AbsoluteFill>
  );
};
